import posthog from 'posthog-js';
import { getImpersonationUserId } from '../../stores/appImpersonationStore';
import {
  AllRestaurantsOption,
  isAllRestaurantsSelected,
} from '../../stores/appUserDefaultStore';
import { FrontendRestaurant } from '../restaurant/restaurantService';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';
const IS_DISABLED = process.env.REACT_APP_ANALYTICS_ENABLED !== 'true';

// While a super admin impersonates an account, suppress all analytics so the
// maintenance actions never pollute the impersonated client's product data.
const shouldSuppress = () => getImpersonationUserId() !== null;

/**
 * Initialize PostHog analytics with cookieless configuration.
 * Uses memory persistence to avoid cookies/localStorage.
 * Disabled in development to avoid polluting production data.
 */
export const initPostHog = () => {
  if (IS_DISABLED) return; // Skip analytics in local development

  if (!POSTHOG_KEY) {
    console.warn('PostHog key not configured - analytics disabled');
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    persistence: 'memory', // Cookieless mode - no localStorage/cookies
    capture_pageview: false, // We handle manually for SPA routing
    capture_pageleave: true,
    autocapture: true, // Auto-track clicks, form submissions
    disable_session_recording: true, // Disabled for now
  });

  // Source discriminator — the RN apps register the same keys with
  // 'cloud_count' | 'culinary_cloud_recipes' | 'hells_kitchen' / 'ios' | 'android'.
  // Lets one PostHog project be filtered per app.
  posthog.register({ app: 'culinary_cloud', app_platform: 'web' });
};

/**
 * Identify a user after authentication.
 * Links anonymous events to the user's identity.
 */
export const identifyUser = (userId: string, properties?: {
  email?: string;
  role?: string;
}) => {
  if (IS_DISABLED || !POSTHOG_KEY || shouldSuppress()) return;

  posthog.identify(userId, properties);
};

/**
 * Reset user identity on logout. Clears the registered restaurant context
 * before resetting so the next (anonymous) session never inherits it.
 */
export const resetUser = () => {
  if (IS_DISABLED || !POSTHOG_KEY) return;

  clearRestaurantAnalyticsContext();
  posthog.reset();
};

// Event-time restaurant context registered as PostHog super properties so it
// rides on every event (manual $pageview, autocapture, $pageleave, custom).
// `restaurant_context_version` lets Cloud System reason about the shape over time.
const RESTAURANT_CONTEXT_VERSION = 1 as const;

const RESTAURANT_CONTEXT_KEYS = [
  'restaurant_id',
  'restaurant_scope',
  'cc_admin_id',
  'restaurant_context_version',
] as const;

export type RestaurantAnalyticsContext =
  | {
      restaurant_scope: 'single';
      restaurant_id: string;
      cc_admin_id: string;
      restaurant_context_version: typeof RESTAURANT_CONTEXT_VERSION;
    }
  | {
      restaurant_scope: 'all';
      // Present only when the accessible restaurants share one unambiguous owner.
      cc_admin_id?: string;
      restaurant_context_version: typeof RESTAURANT_CONTEXT_VERSION;
      // restaurant_id is intentionally absent for the 'all' scope.
    };

/**
 * The one unambiguous owner/admin id across the accessible restaurants, or
 * undefined when they span multiple owners (then All Restaurants activity stays
 * unattributed diagnostic data).
 */
const getUnambiguousCommonOwnerId = (
  restaurants: FrontendRestaurant[]
): string | undefined => {
  const ownerIds = new Set(restaurants.map(r => r.userId));
  return ownerIds.size === 1 ? Array.from(ownerIds)[0] : undefined;
};

/**
 * Build the event-time restaurant context for the current selection. Pure so it
 * can be unit-tested without PostHog. Returns null before a selection resolves.
 */
export const buildRestaurantAnalyticsContext = (
  selected: FrontendRestaurant | AllRestaurantsOption | null,
  restaurants: FrontendRestaurant[]
): RestaurantAnalyticsContext | null => {
  if (!selected) return null;

  if (isAllRestaurantsSelected(selected)) {
    const cc_admin_id = getUnambiguousCommonOwnerId(restaurants);
    return {
      restaurant_scope: 'all',
      ...(cc_admin_id ? { cc_admin_id } : {}),
      restaurant_context_version: RESTAURANT_CONTEXT_VERSION,
    };
  }

  return {
    restaurant_scope: 'single',
    restaurant_id: selected.id,
    cc_admin_id: selected.userId,
    restaurant_context_version: RESTAURANT_CONTEXT_VERSION,
  };
};

/**
 * Register the restaurant context as super properties. Because super properties
 * only ever merge, any key that must be absent for the new scope is explicitly
 * unregistered first (e.g. restaurant_id when switching to All Restaurants).
 */
export const setRestaurantAnalyticsContext = (context: RestaurantAnalyticsContext) => {
  if (IS_DISABLED || !POSTHOG_KEY || shouldSuppress()) return;

  if (context.restaurant_scope === 'all') {
    posthog.unregister('restaurant_id');
    if (context.cc_admin_id === undefined) {
      posthog.unregister('cc_admin_id');
    }
  }

  posthog.register(context);
};

/**
 * Remove all registered restaurant-context super properties.
 */
export const clearRestaurantAnalyticsContext = () => {
  if (IS_DISABLED || !POSTHOG_KEY) return;

  RESTAURANT_CONTEXT_KEYS.forEach(key => posthog.unregister(key));
};

/**
 * Track a page view. Call this on route changes in SPA.
 */
export const trackPageView = (path?: string) => {
  if (IS_DISABLED || !POSTHOG_KEY || shouldSuppress()) return;

  posthog.capture('$pageview', path ? { $current_url: path } : undefined);
};

/**
 * Track a custom event.
 */
export const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
  if (IS_DISABLED || !POSTHOG_KEY || shouldSuppress()) return;

  posthog.capture(eventName, properties);
};

export { posthog };
